import React, { type ReactElement } from 'react';
import { Route, Switch } from 'react-router';

import FeatureRequired from '../../common/components/FeatureRequired';
import Protected from '../../common/components/Protected';
import NotOnPrem from '../../common/components/NotOnPrem';
import ServerCapabilities from '../../common/components/ServerCapabilities';
import { PRODUCT_URLS, ProductSlugs } from '../../common/constants';
import NotFound from '../../common/pages/NotFound';
import Plan from '../containers/Plan';
import Home from '../pages/Home';
import Test from '../pages/Test';
import { TestProvider } from '../contexts/Test';
import { UserFlowProvider } from '../contexts/UserFlow';
import { ScreenshotProvider } from '../../common/contexts/Screenshot';
import Issues from '../pages/Issues';
import UserFlow from '../pages/UserFlow';
import UserFlowIssues from '../pages/UserFlowIssues';
import Walkthrough from '../pages/Walkthrough';
import CustomIntegrationHandler from '../pages/CustomIntegrationHandler';
import IssuesV2 from '../pages/IssuesV2';
import CustomIntegrationExtensionNotInstalled from '../pages/CustomIntegrationExtensionNotInstalled';
import { ExtensionInstalledProvider } from '../contexts/ExtensionInstalled';

const AxeDevToolsRoutes = (): ReactElement => {
  return (
    <ExtensionInstalledProvider>
      <Switch>
        <Route
          exact
          path={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].plans}
        >
          <NotOnPrem>
            <FeatureRequired featureFlagId="billing_v1">
              <Plan />
            </FeatureRequired>
          </NotOnPrem>
        </Route>
        <Route
          exact
          path={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root}
        >
          <Protected>
            <Home />
          </Protected>
        </Route>
        <Route
          path={`${
            PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
          }/tests/:testId`}
          render={({ match }) => {
            const { testId } = match.params as { testId: string };
            return (
              <TestProvider testId={testId}>
                <Route
                  exact
                  path={`${
                    PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
                  }/tests/:testId`}
                >
                  <FeatureRequired featureFlagId="shared_tests_v1">
                    <Test />
                  </FeatureRequired>
                </Route>
                <Route
                  exact
                  path={`${
                    PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
                  }/tests/:testId/issues`}
                >
                  <ScreenshotProvider>
                    <FeatureRequired
                      featureFlagId="launchpad_extension_listissues_v1"
                      off={
                        <FeatureRequired featureFlagId="shared_tests_v1">
                          <Issues />
                        </FeatureRequired>
                      }
                    >
                      <IssuesV2 />
                    </FeatureRequired>
                  </ScreenshotProvider>
                </Route>
              </TestProvider>
            );
          }}
        />
        <Route
          path={`${
            PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
          }/user-flows/:userFlowId`}
          render={({ match }) => {
            const { userFlowId } = match.params as { userFlowId: string };
            return (
              <UserFlowProvider id={userFlowId}>
                <Route
                  exact
                  path={`${
                    PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
                  }/user-flows/:userFlowId`}
                >
                  <FeatureRequired featureFlagId="user_flow_analysis_v2">
                    <ServerCapabilities requireJazzband>
                      <UserFlow />
                    </ServerCapabilities>
                  </FeatureRequired>
                </Route>
                <Route
                  exact
                  path={`${
                    PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
                  }/user-flows/:userFlowId/issues`}
                >
                  <FeatureRequired featureFlagId="user_flow_analysis_v2">
                    <UserFlowIssues />
                  </FeatureRequired>
                </Route>
              </UserFlowProvider>
            );
          }}
        />
        <Route
          exact
          path={`${
            PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
          }/walkthrough`}
        >
          <Walkthrough />
        </Route>
        <Route
          exact
          path={`${
            PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
          }/test-setup`}
        >
          <FeatureRequired featureFlagId="custom_integration_v1">
            <CustomIntegrationHandler />
          </FeatureRequired>
        </Route>
        <Route
          exact
          path={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].notInstalled}
        >
          <FeatureRequired featureFlagId="custom_integration_v1">
            <CustomIntegrationExtensionNotInstalled />
          </FeatureRequired>
        </Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </ExtensionInstalledProvider>
  );
};

export default AxeDevToolsRoutes;
